import React, { memo, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { validators } from 'investira.sdk';
import { Stack, LoadingButton, Icon, Divider } from 'investira.react.components';
import { PageTemplate } from '../../../components/templates';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import { AVALIACAO_SESSOES_QUESTOES, AVALIACAO_QUESTOES, AVALIACAO_MODELO } from '../../../enums';
import AvalicoesSaveStatus from '../_components/AvalicoesSaveStatus';
import SessaoNavProgress from '../_components/SessaoNavProgress';
import AvaliacoesEditSugestoes from './AvaliacoesEditSugestoes';
import AvaliacoesEditQuestoes from './AvaliacoesEditQuestoes';
import AvaliacoesProgresso from '../_components/AvaliacoesProgresso';

const AvaliacoesEdit = memo(props => {
  const { state, actions } = useAvaliacoesContext();
  const {
    sessoes,
    gestor,
    activeCategoryId,
    activeQuestionId,
    activeAliasQuestionId,
    selectedModelo
  } = state;
  const [questoesAvaliadas, setQuestoesAvaliadas] = useState({});

  const navigate = useNavigate();
  const location = useLocation();

  const xModeloSelecionado = AVALIACAO_MODELO[selectedModelo];
  const xModeloLabel = xModeloSelecionado?.label;
  const xModeloSessoes = xModeloSelecionado?.sessoes || Object.keys(sessoes);

  const xQuestoesDisponiveis = useMemo(() => {
    return Object.values(AVALIACAO_QUESTOES).filter(xQuestao => {
      if (!xModeloLabel) return true;
      return (xQuestao?.modelos || []).includes(xModeloLabel);
    });
  }, [xModeloLabel]);

  const xQuestoesDisponiveisIds = useMemo(
    () => new Set(xQuestoesDisponiveis.map(xQuestao => xQuestao.id)),
    [xQuestoesDisponiveis]
  );

  const xQuestoesPorSessao = useMemo(() => {
    return Object.fromEntries(
      Object.values(sessoes).map(xSessao => {
        const xQuestoesSessao = (AVALIACAO_SESSOES_QUESTOES[xSessao.id]?.questoes || []).filter(
          xQuestao => xQuestoesDisponiveisIds.has(xQuestao.id)
        );

        return [
          xSessao.id,
          {
            ...(AVALIACAO_SESSOES_QUESTOES[xSessao.id] || {}),
            questoes: xQuestoesSessao
          }
        ];
      })
    );
  }, [sessoes, xQuestoesDisponiveisIds]);

  const xSessoesDisponiveis = useMemo(() => {
    return Object.fromEntries(
      Object.values(sessoes)
        .filter(xSessao => xModeloSessoes.includes(xSessao.id))
        .map(xSessao => [xSessao.id, xSessao])
    );
  }, [sessoes, xModeloSessoes]);

  const xSessoesDisponiveisIds = useMemo(
    () => new Set(Object.keys(xSessoesDisponiveis)),
    [xSessoesDisponiveis]
  );
  const xSessoesQuestoes = xQuestoesPorSessao[activeCategoryId] || { questoes: [] };
  const xQuestoesSessaoAtiva = xSessoesQuestoes.questoes || [];
  const xQuestoesSize = xQuestoesDisponiveis.length;
  const xQuestoesIds = xQuestoesDisponiveisIds;

  const xRespondidas = 0;

  const xRevisadas = useMemo(() => {
    return Object.entries(questoesAvaliadas || {}).reduce((pCount, [pQuestionId, pResposta]) => {
      if (!xQuestoesIds.has(Number(pQuestionId))) return pCount;
      return pResposta === true ? pCount + 1 : pCount;
    }, 0);
  }, [questoesAvaliadas, xQuestoesIds]);

  const xSessoesProgress = useMemo(() => {
    return Object.fromEntries(
      Object.values(xSessoesDisponiveis).map(xSessao => {
        const xQuestoesSessao = xQuestoesPorSessao[xSessao.id]?.questoes || [];
        const xTotalSessao = xQuestoesSessao.length;
        const xRevisadasSessao = xQuestoesSessao.reduce((pCount, pQuestao) => {
          return questoesAvaliadas?.[pQuestao.id] === true ? pCount + 1 : pCount;
        }, 0);

        const xProgression = xTotalSessao ? (xRevisadasSessao / xTotalSessao) * 100 : 0;

        return [
          xSessao.id,
          {
            ...xSessao,
            progression: xProgression,
            completed: xTotalSessao > 0 && xRevisadasSessao >= xTotalSessao
          }
        ];
      })
    );
  }, [xSessoesDisponiveis, xQuestoesPorSessao, questoesAvaliadas]);

  const xActiveAliasQuestionId = useMemo(() => {
    const xSessaoAtual = sessoes[activeCategoryId];
    if (!xSessaoAtual) return activeAliasQuestionId;

    const xQuestionIndex = xQuestoesSessaoAtiva.findIndex(
      xQuestao => xQuestao.id === activeQuestionId
    );
    if (xQuestionIndex === -1) return activeAliasQuestionId;

    return `${xSessaoAtual.sessao}.${xQuestionIndex + 1}`;
  }, [sessoes, activeCategoryId, activeQuestionId, activeAliasQuestionId, xQuestoesSessaoAtiva]);

  useEffect(() => {
    const xPrimeiraSessaoDisponivel = Object.keys(xSessoesDisponiveis)[0];
    if (!xPrimeiraSessaoDisponivel) return;

    if (!xSessoesDisponiveisIds.has(activeCategoryId)) {
      actions.handleChangeActiveSessaoId(xPrimeiraSessaoDisponivel);
      const xPrimeiraQuestao = xQuestoesPorSessao[xPrimeiraSessaoDisponivel]?.questoes?.[0];
      if (xPrimeiraQuestao) {
        actions.handleChangeActiveQuestionId(xPrimeiraQuestao.id);
      }
      return;
    }

    const xQuestoesSessaoAtual = xQuestoesPorSessao[activeCategoryId]?.questoes || [];
    const xQuestaoAtivaDisponivel = xQuestoesSessaoAtual.some(
      xQuestao => xQuestao.id === activeQuestionId
    );

    if (!xQuestaoAtivaDisponivel && xQuestoesSessaoAtual[0]) {
      actions.handleChangeActiveQuestionId(xQuestoesSessaoAtual[0].id);
    }
  }, [
    actions,
    activeCategoryId,
    activeQuestionId,
    xSessoesDisponiveis,
    xSessoesDisponiveisIds,
    xQuestoesPorSessao
  ]);

  const handleQuestionChange = (pQuestionId, pData = {}) => {
    setQuestoesAvaliadas(pPrev => ({
      ...pPrev,
      [pQuestionId]: Boolean(pData?.avaliado)
    }));
  };

  const handleSelectSessao = (_pEvent, pSessao, pIndex) => {
    !validators.isNull(pSessao) && actions.handleChangeActiveSessaoId(pSessao.id);

    const xQuestoes = xQuestoesPorSessao[pSessao.id]?.questoes;
    if (!validators.isEmpty(xQuestoes)) {
      const xFirstSessaoQuestionId = xQuestoes[0].id;
      actions.handleChangeActiveQuestionId(xFirstSessaoQuestionId);
    } else {
      // TODO: Adiconar feedback para o usuário
      console.log('Falha no carregamento das questões');
    }
  };

  return (
    <PageTemplate
      title={gestor.pessoa_fantasia || 'EXEMPLO'}
      subtitle={'Avaliação Gestor'}
      maxWidth={false}
      headerRenderActions={
        <Stack direction="row" spacing={2} alignItems="center">
          <LoadingButton
            variant="outlined"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
            size="small"
            sx={{ px: 2 }}
            type="submit"
            disabled={props.disabledButtons}
            //loading={isSubmitting}
          >
            {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
            Salvar Rascunho
          </LoadingButton>
          <LoadingButton
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="execute" size={16} />} // Trocar o icone enquanto estiver atualizando passivamente
            size="small"
            sx={{ px: 2 }}
            onClick={() => navigate(`${location.pathname}/resultado`)}
            type="button"
            disabled={props.disabledButtons}
            //loading={isSubmitting}
          >
            {/* {isSubmitting ? "Salvando..." : "Salvar Rascunho"} */}
            Processar Avaliação
          </LoadingButton>
        </Stack>
      }>
      <Stack direction="row" gap={6}>
        <Stack
          id="sessoes"
          direction="column"
          width="25%"
          gap={2}
          sx={{
            position: 'sticky',
            top: '-24px',
            alignSelf: 'flex-start',
            maxHeight: 'calc(80vh - 36px)',
            overflowY: 'auto'
          }}>
          <AvaliacoesProgresso
            qtdQuestoes={xQuestoesSize}
            respondidas={xRespondidas}
            revisadas={xRevisadas}
          />
          <Divider />
          <SessaoNavProgress
            sessoes={xSessoesProgress}
            activeCategoryId={activeCategoryId}
            onClick={handleSelectSessao}
          />
        </Stack>
        <Stack direction="column" flexGrow="1" minWidth="40%" maxWidth="40%" gap={2}>
          <Stack direction="row" justifyContent="end">
            <AvalicoesSaveStatus status={0} />
          </Stack>
          <Divider />
          <AvaliacoesEditQuestoes
            categorias={sessoes}
            activeCategoryId={activeCategoryId}
            activeQuestionId={activeQuestionId}
            onSelect={actions.handleChangeActiveQuestionId}
            questoes={xQuestoesSessaoAtiva}
            respostas={questoesAvaliadas}
            onQuestionChange={handleQuestionChange}
          />
        </Stack>
        <Stack
          id="sugestoes"
          direction="column"
          maxWidth="35%"
          minWidth="35%"
          sx={{
            position: 'sticky',
            top: '-24px',
            alignSelf: 'flex-start',
            maxHeight: 'calc(80vh - 32px)',
            overflowY: 'auto'
          }}>
          <AvaliacoesEditSugestoes
            activeCategoryId={activeCategoryId}
            activeQuestionId={activeQuestionId}
            activeAliasQuestionId={xActiveAliasQuestionId}
          />
        </Stack>
      </Stack>
    </PageTemplate>
  );
});

AvaliacoesEdit.displayName = 'AvaliacoesEdit';

export default AvaliacoesEdit;
